// @flow
import * as React from 'react';
import styled from 'styled-components';
import { isDefined } from 'utils/functional';
import { Link as ReactRouterLink } from 'components/RouterLink';
import { trackClicked } from 'utils/analytics';
import type { RouterHistory, Location } from 'react-router';
import { getCityStateString } from 'utils/address';
import scrollElementIntoView from 'utils/scrollIntoView';
import { ButtonBase } from 'components/Button';
import { H4, BodySmall } from 'components/Text';
import ShowModalContainer from 'containers/ShowModalContainer';
import Flex from 'components/Flex';
import Box from 'components/Box';
import Hide from 'components/Hide';
import Icon from 'components/Icon';
import Divider from 'components/Divider';
import { LogoImage } from 'components/Image';
import SearchTile, { OverlayRouterLink } from 'components/SearchTile';
import { dayAndMonth } from 'utils/date';
import type { CurrentUser } from 'api/currentUser';
import { breakpoints, colors, fontWeights, transition } from '../theme';

const Actions = styled(Box)`
  @media (min-width: ${breakpoints[1]}) {
    border-left: 1px solid ${colors.aluminum};
  }
`;

const StyledButton = styled(({ saved, ...props }: { saved?: boolean }) => (
  <ButtonBase {...props} size="small" />
))`
  z-index: 2;
  box-sizing: border-box;
  display: flex;
  padding-left: 0;
  padding-right: 0;
  justify-content: center;
  align-items: center;
  &,
  svg * {
    transition: all ${transition};
  }
  svg {
    margin-right: 4px;
  }
  @media (min-width: ${breakpoints[1]}) {
    width: 100%;
  }
`;

const CtaButton = styled(
  ({
    hit,
    hasBeenContacted,
    loggedIn,
    onClick,
    ...props
  }: {
    hit: Object,
    hasBeenContacted: boolean,
    loggedIn?: boolean,
    onClick: Function
  }) => {
    const asLink = !loggedIn || hasBeenContacted;
    return (
      <StyledButton
        {...props}
        component={asLink && ReactRouterLink}
        to={
          asLink
            ? `${hit.url[USER_LOCALE]}${
                hasBeenContacted ? '' : '?contactModalOpen=t'
              }`
            : ''
        }
        onClick={() => {
          onClick();
          trackClicked('Search results CTA button', { type: hit.type });
        }}
      >
        {hasBeenContacted ? getText('View') : getText('Contact Us')}
      </StyledButton>
    );
  }
)`
  &:hover {
    color: white;
    background: ${colors.waterBlue};
  }
`;

const SaveButton = styled(StyledButton)`
  color: ${colors.battleshipGrey};
  svg {
    * {
      fill: none;
      stroke: currentColor;
      stroke-width: 2px;
    }
  }
  &:hover {
    background: ${colors.platinum};
    svg {
      * {
        fill: ${colors.turtleGreen};
        stroke: ${colors.turtleGreen};
      }
    }
  }
  ${props =>
    props.saved &&
    `
    svg {
      * {
        fill: ${colors.turtleGreen};
        stroke: ${colors.turtleGreen};
      }
    }
  `}
`;

type InfoItem = {
  label: string,
  value: string
};

const InfoList = styled(
  ({ items, className }: { items: Array<InfoItem>, className: string }) => (
    <ul className={className}>
      {items
        .filter(item => item.value.length > 0)
        .map(item => (
          <li key={item.value.replace(/\s/g, '')}>
            <strong>{item.label}:</strong> {item.value}
          </li>
        ))}
    </ul>
  )
)`
  list-style-type: none;
  padding: 0;
  margin: 0 -10px;
  &:not(:first-child) {
    margin-top: 16px;
  }
  li {
    width: 100%;
    flex: 0 0 auto;
    margin-top: 4px;
    padding: 0 10px;
    color: ${colors.battleshipGrey};
    font-size: 14px;
  }
  strong {
    font-weight: ${fontWeights.semiBold};
  }
  @media (min-width: ${breakpoints[1]}) {
    display: flex;
    flex-wrap: wrap;
    li {
      width: 50%;
    }
  }
`;

type HitWithTrack = {
  trackClick: Function,
  name: string,
  url: Object,
  schoolName: string,
  orgUrl: Object,
  logoHandle: string,
  city: string,
  state: string,
  country: string,
  nbOfStudents: number,
  acceptanceRate: number,
  avgFinancialAid: number,
  applicationDeadline: string,
  isPublicSchool: boolean,
  pathToResults: string
};

const HitInfo = ({
  trackClick,
  name,
  url,
  schoolName,
  city,
  logoHandle,
  state,
  country,
  nbOfStudents,
  acceptanceRate,
  avgFinancialAid,
  applicationDeadline,
  isPublicSchool,
  pathToResults
}: HitWithTrack) => (
  <Flex flexDirection={['column', 'row', null, null]}>
    <Flex flex="0 0 auto" pr={[0, 30, null, null]} pb={[24, 0, null, null]}>
      <LogoImage
        alignItems="flex-start"
        width={120}
        handle={logoHandle}
        alt={name}
        initial={schoolName}
      />
    </Flex>
    <Box width={['100%', 'auto', null, null]} flex="1 1 auto">
      <Box>
        <H4 mb={0}>
          <OverlayRouterLink
            data-qa-id="search-result-link"
            to={{
              pathname: url[USER_LOCALE],
              state: {
                showSearchContext: true,
                pathToResults,
                originatingPathname: pathToResults
              }
            }}
            onClick={() => {
              if (trackClick) trackClick();
            }}
          >
            {name}
          </OverlayRouterLink>
        </H4>
        <BodySmall>
          {schoolName && (
            <>
              {schoolName}
              {' | '}
            </>
          )}
          {getCityStateString({ city, stateCode: state, country })}
        </BodySmall>
      </Box>
      <InfoList
        items={[
          {
            value: nbOfStudents ? nbOfStudents.toLocaleString(USER_LOCALE) : '',
            label: getText('Number of students')
          },
          {
            value: acceptanceRate ? `${acceptanceRate}%` : '',
            label: getText('Acceptance rate')
          },
          {
            value: avgFinancialAid
              ? `$${avgFinancialAid.toLocaleString(USER_LOCALE)}`
              : '',
            label: getText('Average financial aid offer')
          },
          {
            value: applicationDeadline ? dayAndMonth(applicationDeadline) : '',
            label: getText('Next application deadline')
          },
          {
            value: isPublicSchool ? getText('Public') : getText('Private'),
            label: getText('Type')
          }
        ]}
      />
    </Box>
  </Flex>
);

type Props = {
  hit: Object,
  scrollIntoView?: boolean,
  history: RouterHistory,
  location: Location,
  saveProgram?: () => void,
  unsaveProgram: () => void,
  saved: boolean,
  canSave: boolean,
  'data-qa-id'?: string,
  trackClick?: () => any,
  currentUser: CurrentUser,
  updateContactedGradPrograms: Function,
  hasBeenContacted: boolean
};

export default class GradSchoolsSearchHit extends React.PureComponent<Props> {
  hitRef = React.createRef<Element>();

  componentDidMount() {
    if (this.props.scrollIntoView && this.hitRef.current) {
      scrollElementIntoView(this.hitRef.current);
    }
  }

  render() {
    const pathToResults = this.props.location.pathname;
    const loggedIn = isDefined(this.props.currentUser.id);
    return (
      <div
        ref={this.hitRef}
        data-qa-id={this.props['data-qa-id'] || 'search-result'}
      >
        <SearchTile
          pr={[undefined, 0, 0, 0]}
          display={['block', null, 'flex']}
          justifyContent="space-between"
        >
          <Box pr={[0, 16]}>
            <HitInfo
              trackClick={this.props.trackClick}
              pathToResults={pathToResults}
              {...this.props.hit}
            />
          </Box>
          <Actions
            width={[undefined, null, 132]}
            mt={[12, null, 0]}
            px={[0, null, 12]}
            ml={[0, 150, 0]}
            display="flex"
            flexDirection={['row', null, 'column']}
            justifyContent={['flex-start', null, 'center']}
            flex="0 0 auto"
            zIndex="2"
          >
            <ShowModalContainer
              render={showModal => (
                <CtaButton
                  color={colors.waterBlue}
                  hit={this.props.hit}
                  hasBeenContacted={this.props.hasBeenContacted}
                  loggedIn={loggedIn}
                  onClick={() => {
                    if (!this.props.hasBeenContacted && loggedIn) {
                      showModal('GRAD_PROGRAM_CONTACT', {
                        currentUser: this.props.currentUser,
                        hasBeenContacted: this.props.hasBeenContacted,
                        updateContactedGradPrograms: this.props
                          .updateContactedGradPrograms,
                        programId: this.props.hit.objectID,
                        degreeProgramsOffered: this.props.hit
                          .degreeProgramsOffered
                      });
                    }
                  }}
                />
              )}
            />
            {this.props.canSave && (
              <>
                <Hide xs sm>
                  <Divider opacity="0.2" />
                </Hide>
                <SaveButton
                  ml={[16, null, 0]}
                  saved={this.props.saved}
                  onClick={e => {
                    e.stopPropagation();
                    if (this.props.saved) {
                      this.props.unsaveProgram();
                    } else if (this.props.saveProgram) {
                      this.props.saveProgram();
                    }
                  }}
                  data-qa-id={
                    this.props.saved ? 'unsave-listing' : 'save-listing'
                  }
                >
                  <Icon name="heart" width={13} />
                  {this.props.saved ? getText('Unsave') : getText('Save')}
                </SaveButton>
              </>
            )}
          </Actions>
        </SearchTile>
      </div>
    );
  }
}
